import React__default from 'react';
import { Page, Document } from 'react-pdf';
import { FileViewerToolbar } from './FileViewerToolbar.js';
import { getBaseProps, getAriaProps } from '../../../helpers/propHelpers.js';
import classNames from 'classnames';

const ACCEPTED_PREVIEW_IMAGE_TYPES = ['gif', 'jpeg', 'jpg', 'png', 'svg'];
class FileViewer extends React__default.Component {
    constructor(props) {
        super(props);
        this.handlePageChange = (value) => {
            const newPage = this.state.currentPage + value;
            if (newPage >= 1 && newPage <= this.state.totalPages) {
                this.setState({
                    currentPage: newPage,
                });
            }
        };
        this.updateScale = (newScale) => {
            if (newScale >= 0.5 && newScale <= 3) {
                this.setState({
                    scale: newScale,
                    autoZoom: false,
                });
            }
        };
        this.handleZoomClick = (value) => {
            const newScale = this.state.scale + value;
            this.updateScale(newScale);
        };
        this.handleZoomChange = (event) => {
            const val = event.target.value;
            if (val === 'auto') {
                this.setState({
                    scale: 1.1,
                    autoZoom: true,
                });
                return;
            }
            const newScale = Number(val);
            this.updateScale(newScale);
        };
        this.handleRotate = () => {
            const newRotation = this.state.rotation + 90;
            this.setState({
                rotation: newRotation > 270 ? 0 : newRotation,
            });
        };
        this.handlePrint = () => {
            try {
                // handle printing on Safari
                window.print();
            }
            catch (_a) {
                window.open(this.state.fileSrc, 'PRINT');
            }
        };
        this.onDocumentLoadSuccess = (props) => {
            this.setState({
                totalPages: Number(props.numPages),
            });
        };
        this.getFilePages = () => {
            const pages = Array.from(new Array(this.state.totalPages), (el, index) => {
                return (React__default.createElement(Page, { key: `page_${index + 1}`, scale: this.state.scale, pageNumber: index + 1, error: this.props.handleError, loading: '' }));
            });
            return pages;
        };
        this.normalizeFile = (file) => {
            // if empty file, just bail
            if (!file) {
                return {
                    fileSrc: null,
                    fileName: null,
                    fileType: null,
                };
            }
            // check if this is an actual file
            if ('File' in window && file instanceof File) {
                const browserFile = {
                    fileSrc: file,
                    fileName: file.name,
                    fileType: this.determineFileType(file.type),
                };
                return browserFile;
            }
            // set to handle BAPI or PAPI file signatures
            const contentType = file.content_type || file.fileType || file.file_type;
            const normalizedFile = {
                fileSrc: file.download_url || file.fileDownloadSrc || file.file_download_src,
                fileName: file.file_name || file.name,
                fileType: this.determineFileType(contentType),
            };
            return normalizedFile;
        };
        this.determineFileType = (contentType) => {
            if (!contentType) {
                return null;
            }
            const fileExt = contentType.split('/').pop() || '';
            if (ACCEPTED_PREVIEW_IMAGE_TYPES.includes(fileExt)) {
                return 'img';
            }
            return fileExt;
        };
        this.handleClose = () => {
            this.setState({
                currentPage: 1,
                totalPages: 1,
                rotation: 0,
                scale: 1,
                autoZoom: false,
                pdfRef: React__default.createRef(),
            });
            if (this.props.handleClose) {
                this.props.handleClose();
            }
        };
        this.isElementInViewport = (el) => {
            const rect = el.getBoundingClientRect();
            return (rect.top >= 0 &&
                rect.left >= 0 &&
                rect.right <= (window.innerWidth || document.documentElement.clientWidth) &&
                rect.bottom <= (window.innerHeight || document.documentElement.clientHeight));
        };
        this.handlePageScroll = () => {
            var _a;
            const nodes = document.querySelectorAll('.react-pdf__Page');
            const pageNumber = ((_a = Array.from(nodes).find(this.isElementInViewport)) === null || _a === void 0 ? void 0 : _a.getAttribute('data-page-number')) || this.state.currentPage;
            this.setState({
                currentPage: Number(pageNumber),
            });
        };
        this.handleImageRotate = () => {
            const imageRotation = this.state.imageRotation;
            this.setState({
                imageRotation: imageRotation + 90,
            });
        };
        this.base64ConvertFile = (file) => {
            const promise = new Promise((resolve) => {
                const reader = new FileReader();
                reader.onload = function (readerEvt) {
                    const binaryString = readerEvt.target.result;
                    const arrStr = binaryString.split(',');
                    let ret = null;
                    if (arrStr && arrStr.length > 1) {
                        ret = readerEvt.target.result.split(',')[1];
                    }
                    resolve(ret);
                };
                reader.readAsDataURL(file);
            });
            return promise;
        };
        this.componentDidMount = () => {
            const file = this.normalizeFile(this.props.file);
            if (typeof (file === null || file === void 0 ? void 0 : file.fileSrc) !== 'string' && file.fileType == 'img') {
                this.base64ConvertFile(this.props.file).then((str) => {
                    const src = `data:image/png;base64, ${str}`;
                    this.setState({
                        fileSrc: src,
                    });
                });
            }
            else {
                this.setState({
                    fileSrc: file.fileSrc,
                    pdfRef: React__default.createRef(),
                });
            }
        };
        this.state = {
            currentPage: 1,
            totalPages: 1,
            rotation: 0,
            scale: 1,
            autoZoom: false,
            pdfRef: React__default.createRef(),
            imageRotation: 0,
            fileSrc: '',
        };
    }
    render() {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
        const file = this.normalizeFile(this.props.file);
        const baseProps = getBaseProps(this.props);
        getAriaProps(this.props);
        const fileSrcAvailable = ((_a = this === null || this === void 0 ? void 0 : this.state) === null || _a === void 0 ? void 0 : _a.fileSrc) && typeof this.state.fileSrc === 'string';
        return (React__default.createElement("div", Object.assign({}, baseProps, { className: classNames((_b = this.props) === null || _b === void 0 ? void 0 : _b.className, 'FileViewer') }),
            React__default.createElement(FileViewerToolbar, { "data-testid": `${(_c = this.props) === null || _c === void 0 ? void 0 : _c['data-testid']}-toolbar` || 'preview-file-toolbar', showCloseButton: this.props.disableClose !== undefined ? !this.props.disableClose : true, showCloseIcon: (_e = (_d = this.props) === null || _d === void 0 ? void 0 : _d.showCloseIcon) !== null && _e !== void 0 ? _e : true, closeButtonText: (_g = (_f = this.props) === null || _f === void 0 ? void 0 : _f.closeButtonText) !== null && _g !== void 0 ? _g : '', downloadUrl: (file === null || file === void 0 ? void 0 : file.fileSrc) || '', fileName: (file === null || file === void 0 ? void 0 : file.fileName) || '', fileType: file === null || file === void 0 ? void 0 : file.fileType, currentPage: this.state.currentPage, totalPages: this.state.totalPages, currentScale: this.state.scale, handlePageChange: this.handlePageChange, handlePreviewClose: this.handleClose, handlePrint: this.handlePrint, handleRotate: this.handleRotate, handleImageRotate: this.handleImageRotate, handleZoomClick: this.handleZoomClick, handleZoomChange: this.handleZoomChange, showAgreementFileActionItems: (_h = this.props) === null || _h === void 0 ? void 0 : _h.showAgreementFileActionItems }),
            React__default.createElement("div", { className: "FileViewer__preview-container", onScroll: this.handlePageScroll }, file.fileType == 'img' ? (React__default.createElement(React__default.Fragment, null, fileSrcAvailable ? (React__default.createElement("img", { src: this.state.fileSrc, className: 'FileViewer__image-preview', style: { transform: `rotate(${this.state.imageRotation}deg)` }, alt: "" })) : (React__default.createElement("div", { className: 'FileViewer__file-processing-notice-wrapper' },
                React__default.createElement("span", { className: 'FileViewer__file-processing-notice' }, "This file is still processing and cannot be previewed."),
                React__default.createElement("span", { className: 'FileViewer__file-processing-notice' }, "Please try again shortly."))))) : file.fileType == 'pdf' ? (React__default.createElement("div", { onScroll: this.handlePageScroll },
                React__default.createElement(Document, { ref: this.state.pdfRef, className: (_k = (_j = this.props) === null || _j === void 0 ? void 0 : _j.classes) === null || _k === void 0 ? void 0 : _k.pdfjsDocument, file: (file === null || file === void 0 ? void 0 : file.fileSrc) || '', onLoadSuccess: this.onDocumentLoadSuccess, 
                    // CLA-19580: do not override the document's rotation
                    // rotate={this.state.rotation}
                    loading: '', error: this.props.handleError, options: { workerSrc: '/pdf.worker.js' } }, this.getFilePages()))) : (''))));
    }
}

export { ACCEPTED_PREVIEW_IMAGE_TYPES, FileViewer, FileViewer as default };
//# sourceMappingURL=FileViewer.js.map
